import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  generateId,
  parseRecipeText,
  addRecipe,
  addIngredients,
  addMethodSteps,
  useT,
  useAppDispatch,
  type Ingredient,
  type MethodStep,
  type ParsedRecipe,
  type Recipe,
} from "@reciperoll/core";
import { ChevronRight, Edit3, HelpCircle, X } from "react-feather";
import { RecipeForm, type RecipeFormHandle } from "../components/RecipeForm";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { importRecipeFromUrl, normalizeUrl } from "../utils/importRecipe";
import { removeRecipeImageByUrl } from "../utils/deleteRecipe";
import { parsedToIngredientRows, parsedToMethodRows } from "../utils/recipeFormRows";

type Step = "choose" | "import" | "paste" | "form";

export default function NewRecipeForm() {
  const t = useT();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // The recipe id is assigned up front so a URL-imported image is re-hosted under
  // it (matching user-uploaded images); the form reuses it on save.
  const recipeId = useRef(generateId()).current;

  const [step, setStep] = useState<Step>("choose");
  const [parsed, setParsed] = useState<ParsedRecipe | undefined>();

  // Import-from-URL state
  const [url, setUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  // Quick-paste state. The raw text is kept so the form can offer "Edit text" to
  // jump back and re-parse after OCR/format fixes (matches RN's sourceText path).
  const [pasteText, setPasteText] = useState("");

  // Paste format-help modal.
  const [helpOpen, setHelpOpen] = useState(false);

  // Discard/edit-text confirmations, gated on the form's isDirty handle.
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmEditText, setConfirmEditText] = useState(false);
  const formRef = useRef<RecipeFormHandle>(null);

  function handleImport() {
    if (!url.trim()) return;
    setImporting(true);
    setImportError(null);
    importRecipeFromUrl(normalizeUrl(url), recipeId)
      .then((result) => {
        if (result.ok) {
          setParsed(result.recipe);
          setStep("form");
        } else if (result.pasteText) {
          // Couldn't auto-extract, but we have the page's text — drop it into
          // Quick Paste pre-filled so the user can tidy it, not a dead end.
          setPasteText(result.pasteText);
          setStep("paste");
        } else {
          setImportError(result.reason);
        }
      })
      .finally(() => setImporting(false));
  }

  function handleParsePaste() {
    if (!pasteText.trim()) return;
    setParsed(parseRecipeText(pasteText));
    setStep("form");
  }

  // Jump back to the paste step (text preserved) so the user can fix OCR/format
  // mistakes and re-parse. Confirm first if the form has been edited, since
  // re-parsing rebuilds the draft from scratch. Only offered when the form was
  // reached via import/paste (there is raw pasteText).
  function goEditText() {
    setStep("paste");
  }

  function handleEditText() {
    if (!pasteText.trim()) return;
    if (formRef.current?.isDirty()) setConfirmEditText(true);
    else goEditText();
  }

  function handleSave(recipe: Recipe, ingredients: Ingredient[], methodSteps: MethodStep[]) {
    dispatch(addRecipe({ ...recipe, id: recipeId }));
    dispatch(addIngredients({ recipeId, ingredients }));
    dispatch(addMethodSteps({ recipeId, methodSteps }));
    navigate(`/recipe/${recipeId}`);
  }

  function doCancel() {
    // An import may have re-hosted an image under this (unsaved) recipe's id;
    // drop it so it doesn't orphan in Storage.
    removeRecipeImageByUrl(parsed?.image);
    navigate("/recipes");
  }

  function handleCancel() {
    if (formRef.current?.isDirty()) setConfirmCancel(true);
    else doCancel();
  }

  if (step === "form") {
    return (
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        {/* Reached via import/paste → let the user go back and re-parse after fixes. */}
        {pasteText.trim() && (
          <button
            type="button"
            onClick={handleEditText}
            className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-terracotta-ink hover:opacity-80 cursor-pointer"
          >
            <Edit3 className="w-3.5 h-3.5" />
            {t("addRecipe.editText")}
          </button>
        )}
        <RecipeForm
          ref={formRef}
          recipeId={recipeId}
          mode="create"
          initialRecipe={
            parsed && {
              title: parsed.title,
              image: parsed.image,
              sourceUrl: parsed.sourceUrl,
              cookingDuration: parsed.cookingDuration,
              difficulty: parsed.difficulty,
              servings: parsed.servings,
              dietary: parsed.dietary,
              notes: parsed.notes,
            }
          }
          initialIngredients={parsed ? parsedToIngredientRows(parsed) : undefined}
          initialMethod={parsed ? parsedToMethodRows(parsed) : undefined}
          onSubmit={handleSave}
          onCancel={handleCancel}
        />

        {confirmCancel && (
          <ConfirmDialog
            title={t("addRecipe.discardTitle")}
            message={t("addRecipe.discardMessage")}
            confirmLabel={t("addRecipe.discard")}
            cancelLabel={t("addRecipe.keepEditing")}
            confirmClass="bg-red-500 hover:bg-red-600"
            onConfirm={() => {
              setConfirmCancel(false);
              doCancel();
            }}
            onCancel={() => setConfirmCancel(false)}
          />
        )}

        {confirmEditText && (
          <ConfirmDialog
            title={t("addRecipe.editTextTitle")}
            message={t("addRecipe.editTextMessage")}
            confirmLabel={t("addRecipe.editText")}
            cancelLabel={t("addRecipe.keepEditing")}
            confirmClass="bg-red-500 hover:bg-red-600"
            onConfirm={() => {
              setConfirmEditText(false);
              goEditText();
            }}
            onCancel={() => setConfirmEditText(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 py-10">
      <h1 className="font-display text-3xl font-extrabold text-ink mb-6">
        {t("addRecipe.title")}
      </h1>

      {step === "choose" && (
        <div className="space-y-3">
          <ChooserCard
            title={t("addRecipe.importFromUrl")}
            subtitle={t("addRecipe.importSubtitle")}
            onClick={() => setStep("import")}
          />
          <ChooserCard
            title={t("addRecipe.quickPaste")}
            subtitle={t("addRecipe.pasteSubtitle")}
            onClick={() => setStep("paste")}
          />
          <ChooserCard
            title={t("addRecipe.enterManually")}
            subtitle={t("addRecipe.manualSubtitle")}
            onClick={() => setStep("form")}
          />
        </div>
      )}

      {step === "import" && (
        <div>
          <p className="text-sm text-ink-soft mb-5 leading-relaxed">
            {t("addRecipe.idleDescription")}
          </p>
          <label className="block text-sm font-semibold text-ink mb-1">
            {t("addRecipe.recipeUrl")}
          </label>
          <input
            className="w-full rounded-lg border border-line bg-surface dark:bg-surface-2 px-3 py-2 outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleImport()}
            placeholder="https://…"
            autoFocus
          />
          <p className="mt-2 text-xs text-ink-faint leading-relaxed">
            {t("addRecipe.importBlockedNote")}
          </p>
          {importError && <p className="mt-3 text-sm text-red-600">{importError}</p>}
          <div className="mt-5 flex gap-2">
            <button
              onClick={() => setStep("choose")}
              className="rounded-xl px-4 py-2.5 text-sm font-semibold text-ink-soft cursor-pointer hover:text-ink"
            >
              {t("common.back")}
            </button>
            <button
              onClick={handleImport}
              disabled={!url.trim() || importing}
              className="rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50"
            >
              {importing ? t("addRecipe.importingShort") : t("addRecipe.importShort")}
            </button>
          </div>
        </div>
      )}

      {step === "paste" && (
        <div>
          <div className="flex items-start justify-between gap-3 mb-1">
            <label className="block text-sm font-semibold text-ink">
              {t("addRecipe.pasteYourRecipe")}
            </label>
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              aria-label={t("addRecipe.formatHelpTitle")}
              className="shrink-0 pt-0.5 text-terracotta-ink hover:opacity-80 cursor-pointer"
            >
              <HelpCircle className="w-[18px] h-[18px]" />
            </button>
          </div>
          <p className="text-xs text-ink-faint mb-1 leading-relaxed">
            {t("addRecipe.pasteHelp")}
          </p>
          <textarea
            className="w-full rounded-lg border border-line bg-surface dark:bg-surface-2 px-3 py-2 text-sm outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta"
            rows={14}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={t("addRecipe.pastePlaceholder")}
            autoFocus
          />
          <div className="mt-5 flex gap-2">
            <button
              onClick={() => setStep("choose")}
              className="rounded-xl px-4 py-2.5 text-sm font-semibold text-ink-soft cursor-pointer hover:text-ink"
            >
              {t("common.back")}
            </button>
            <button
              onClick={handleParsePaste}
              disabled={!pasteText.trim()}
              className="rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50"
            >
              {t("common.continue")}
            </button>
          </div>

          {helpOpen && <FormatHelpModal onClose={() => setHelpOpen(false)} />}
        </div>
      )}
    </div>
  );
}

// Paste format cheatsheet — the syntax parseRecipeText understands. Mirrors RN's
// RecipeFormatHelp (same i18n keys) so the content localises identically.
function FormatHelpModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md max-h-[80vh] overflow-y-auto rounded-2xl bg-surface p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-ink">{t("addRecipe.formatHelpTitle")}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="text-ink-faint hover:text-ink cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-ink-soft mb-4 leading-relaxed">
          {t("addRecipe.formatHelpIntro")}
        </p>
        <FormatTip
          title={t("addRecipe.formatHelpSectionsTitle")}
          example={"Ingredients\nMethod\nNotes"}
          body={t("addRecipe.formatHelpSectionsBody")}
        />
        <FormatTip
          title={t("addRecipe.formatHelpGroupsTitle")}
          example={"[Sauce]\nFor the topping\nSauce:"}
          body={t("addRecipe.formatHelpGroupsBody")}
        />
        <FormatTip
          title={t("addRecipe.formatHelpDetailsTitle")}
          example={"Serves: 4\nTime: 30 mins"}
          body={t("addRecipe.formatHelpDetailsBody")}
        />
      </div>
    </div>
  );
}

function FormatTip({ title, example, body }: { title: string; example: string; body: string }) {
  return (
    <div className="mb-5 last:mb-0">
      <p className="text-base font-bold text-ink mb-1.5">{title}</p>
      <pre className="bg-surface-2 rounded-lg px-3 py-2 mb-1.5 font-mono text-sm text-ink whitespace-pre-wrap leading-relaxed">
        {example}
      </pre>
      <p className="text-sm text-ink-soft leading-relaxed">{body}</p>
    </div>
  );
}

function ChooserCard({
  title,
  subtitle,
  onClick,
}: {
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left flex items-center gap-4 rounded-2xl p-4 bg-surface cursor-pointer hover:bg-surface-2 transition-colors border border-line"
    >
      <div className="flex-1">
        <p className="text-base font-bold text-ink">{title}</p>
        <p className="text-sm text-ink-soft mt-0.5">{subtitle}</p>
      </div>
      <ChevronRight className="w-5 h-5 text-ink-faint" />
    </button>
  );
}
